/**
 * Composant - ReportsPage
 *
 * Rapport d'activité de caisse du caissier connecté, sur une période (source : historique CBS).
 * - indicateurs : versé, retiré, flux net, opérations, montants moyens, plus grosse opération ;
 * - graphique des volumes journaliers ;
 * - récapitulatif par jour (totaux), principaux clients, motifs de retrait ;
 * - exports : CSV (récapitulatif journalier, détail des opérations) et rapport A4 imprimable
 *   avec cases de signature (caissier, chef d'agence).
 */

import { useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  Printer,
  RefreshCw,
  Scale,
  Trophy,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useOperationHistory } from '../../hooks/useOperationHistory';
import { useAuth } from '../../contexts/AuthContext';
import logoUnacoopec from '../../assets/logo-unacoopec.jpg';
import {
  type Periode,
  type PeriodeRapide,
  dateCourte,
  depuisIso,
  echapperHtml,
  exporterOperationsCsv,
  fcfa,
  filtrer,
  imprimerHtml,
  iso,
  libelleJour,
  libellePeriode,
  nombre,
  parJour,
  parMotif,
  periodeRapide,
  principauxClients,
  telechargerCsv,
  totaux,
  type LigneJour,
} from '../history/analyseOperations';
import '../history/pages.css';

const PERIODES: { code: PeriodeRapide; libelle: string }[] = [
  { code: 'jour', libelle: "Aujourd'hui" },
  { code: 'hier', libelle: 'Hier' },
  { code: 'semaine', libelle: 'Cette semaine' },
  { code: 'mois', libelle: 'Ce mois' },
  { code: 'mois-precedent', libelle: 'Mois précédent' },
  { code: '30j', libelle: '30 derniers jours' },
];

const VERT = '#016951';
const ROSE = '#e11d48';
const COMPACT = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 });

export function ReportsPage() {
  const { transactions, loading, error, refresh } = useOperationHistory();
  const { user } = useAuth();
  const nomCaissier = user?.displayName || user?.username || '';
  const codeCaissier = user?.cardData?.code || user?.username || '';

  const [codePeriode, setCodePeriode] = useState<PeriodeRapide | 'perso'>('30j');
  const [periode, setPeriode] = useState<Periode>(periodeRapide('30j'));

  const ops = useMemo(() => filtrer(transactions, { periode }), [transactions, periode]);
  const k = useMemo(() => totaux(ops), [ops]);
  const jours = useMemo(() => parJour(ops), [ops]);
  const clients = useMemo(() => principauxClients(ops, 5), [ops]);
  const motifsRetrait = useMemo(() => parMotif(ops, 'retrait', 6), [ops]);

  // Graphique : jours dans l'ordre chronologique
  const donneesGraphique = useMemo(
    () =>
      [...jours].reverse().map((j) => ({
        jour: dateCourte(j.jour).slice(0, 5),
        Versements: j.montantVersements,
        Retraits: j.montantRetraits,
      })),
    [jours],
  );

  const choisirPeriode = (code: PeriodeRapide) => {
    setCodePeriode(code);
    setPeriode(periodeRapide(code));
  };
  const majDate = (champ: keyof Periode, valeur: string) => {
    setCodePeriode('perso');
    setPeriode((p) => ({ ...p, [champ]: valeur }));
  };

  const suffixe = `${periode.du || 'debut'}_${periode.au || iso(new Date())}`;
  const exporterJournalier = () =>
    telechargerCsv(
      `rapport-journalier-${suffixe}.csv`,
      ['Date', 'Nb versements', 'Montant versé (FCFA)', 'Nb retraits', 'Montant retiré (FCFA)', 'Flux net (FCFA)'],
      [
        ...[...jours].reverse().map((j) => [dateCourte(j.jour), j.nbVersements, j.montantVersements, j.nbRetraits, j.montantRetraits, j.net]),
        ['TOTAL', k.nbVersements, k.montantVersements, k.nbRetraits, k.montantRetraits, k.net],
      ],
    );
  const exporterDetail = () => exporterOperationsCsv(ops, `operations-${suffixe}.csv`);
  const imprimerRapport = () =>
    imprimerHtml(htmlRapport({ periode: libellePeriode(periode), caissier: nomCaissier, code: codeCaissier, k, jours, clients, motifsRetrait }));

  const vide = !loading && ops.length === 0;

  return (
    <div className="pg">
      <header className="pg-entete">
        <div className="titre">
          <span className="ico">
            <BarChart3 />
          </span>
          <div>
            <h1>Rapport d&apos;activité de caisse</h1>
            <p>
              {libellePeriode(periode)}
              {nomCaissier && ` · ${nomCaissier}`}
            </p>
          </div>
        </div>
        <div className="pg-actions">
          <button type="button" className="pg-btn" onClick={() => refresh()} disabled={loading}>
            <RefreshCw className={loading ? 'pg-tourne' : undefined} /> Actualiser
          </button>
          <button type="button" className="pg-btn" onClick={exporterDetail} disabled={loading || vide}>
            <FileSpreadsheet /> Détail (CSV)
          </button>
          <button type="button" className="pg-btn" onClick={exporterJournalier} disabled={loading || vide}>
            <Download /> Récapitulatif (CSV)
          </button>
          <button type="button" className="pg-btn principal" onClick={imprimerRapport} disabled={loading || vide}>
            <Printer /> Imprimer le rapport
          </button>
        </div>
      </header>

      {/* Période */}
      <section className="pg-filtres" aria-label="Période du rapport">
        <div className="pg-ligne">
          <div className="pg-segments" role="group" aria-label="Période">
            {PERIODES.map((p) => (
              <button key={p.code} type="button" className={codePeriode === p.code ? 'actif' : ''} onClick={() => choisirPeriode(p.code)}>
                {p.libelle}
              </button>
            ))}
          </div>
          <div className="pg-dates">
            <label htmlFor="rap-du">Du</label>
            <input id="rap-du" type="date" className="pg-input" value={periode.du} max={periode.au || undefined} onChange={(e) => majDate('du', e.target.value)} />
            <label htmlFor="rap-au">au</label>
            <input id="rap-au" type="date" className="pg-input" value={periode.au} min={periode.du || undefined} onChange={(e) => majDate('au', e.target.value)} />
          </div>
        </div>
      </section>

      {error && (
        <div className="pg-erreur" role="alert">
          <AlertCircle />
          <div>
            <strong>Les données n&apos;ont pas pu être chargées.</strong> {error}
          </div>
        </div>
      )}

      {/* Indicateurs */}
      <section className="pg-kpis" aria-label="Indicateurs de la période">
        <Kpi ton="vert" icone={<ArrowDownLeft />} titre="Total versé" valeur={fcfa(k.montantVersements)} sous={`${nombre(k.nbVersements)} versement(s) · moyenne ${fcfa(k.moyenneVersement)}`} chargement={loading} />
        <Kpi ton="rose" icone={<ArrowUpRight />} titre="Total retiré" valeur={fcfa(k.montantRetraits)} sous={`${nombre(k.nbRetraits)} retrait(s) · moyenne ${fcfa(k.moyenneRetrait)}`} chargement={loading} />
        <Kpi
          ton="bleu"
          icone={<Scale />}
          titre="Flux net de caisse"
          valeur={`${k.net > 0 ? '+' : ''}${fcfa(k.net)}`}
          classeValeur={k.net >= 0 ? 'pg-pos' : 'pg-neg'}
          sous={k.net >= 0 ? 'Entrées supérieures aux sorties' : 'Sorties supérieures aux entrées'}
          chargement={loading}
        />
        <Kpi
          ton="gris"
          icone={<Trophy />}
          titre="Opérations"
          valeur={nombre(k.nbOperations)}
          sous={k.plusGrosse ? `Plus grosse : ${fcfa(k.plusGrosse.amount)} (${k.plusGrosse.type})` : 'Aucune opération'}
          chargement={loading}
        />
      </section>

      {vide ? (
        <section className="pg-carte">
          <div className="pg-vide">
            <FileText />
            <strong>Aucune opération sur cette période</strong>
            <p>Choisissez une autre période pour générer le rapport.</p>
          </div>
        </section>
      ) : (
        <>
          {/* Graphique */}
          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Volumes journaliers</h2>
              <span>Montants versés et retirés par jour</span>
            </div>
            <div className="pg-carte-corps" style={{ height: 300 }}>
              {loading ? (
                <div className="pg-squelette" style={{ height: '100%' }} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={donneesGraphique} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="jour" tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => COMPACT.format(v)} width={56} />
                    <Tooltip
                      formatter={(v: number) => fcfa(v)}
                      cursor={{ fill: 'rgba(1,105,81,0.05)' }}
                      contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 13 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Bar dataKey="Versements" fill={VERT} radius={[4, 4, 0, 0]} maxBarSize={36} />
                    <Bar dataKey="Retraits" fill={ROSE} radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          {/* Récapitulatif journalier */}
          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Récapitulatif par jour</h2>
              <span>{jours.length} jour(s) d&apos;activité</span>
            </div>
            <div className="pg-table-wrap">
              <table className="pg-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="droite">Versements</th>
                    <th className="droite">Montant versé</th>
                    <th className="droite">Retraits</th>
                    <th className="droite">Montant retiré</th>
                    <th className="droite">Flux net</th>
                  </tr>
                </thead>
                <tbody>
                  {jours.map((j) => (
                    <tr key={j.jour}>
                      <td>{libelleJour(j.jour)}</td>
                      <td className="droite num">{j.nbVersements}</td>
                      <td className="droite num pg-pos">{fcfa(j.montantVersements)}</td>
                      <td className="droite num">{j.nbRetraits}</td>
                      <td className="droite num pg-neg">{fcfa(j.montantRetraits)}</td>
                      <td className={`droite num ${j.net >= 0 ? 'pg-pos' : 'pg-neg'}`}>{fcfa(j.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total de la période</td>
                    <td className="droite num">{k.nbVersements}</td>
                    <td className="droite num pg-pos">{fcfa(k.montantVersements)}</td>
                    <td className="droite num">{k.nbRetraits}</td>
                    <td className="droite num pg-neg">{fcfa(k.montantRetraits)}</td>
                    <td className={`droite num ${k.net >= 0 ? 'pg-pos' : 'pg-neg'}`}>{fcfa(k.net)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <div className="pg-grille-2">
            {/* Principaux clients */}
            <section className="pg-carte">
              <div className="pg-carte-tete">
                <h2>Principaux clients</h2>
                <span>Par volume traité</span>
              </div>
              <div className="pg-table-wrap">
                <table className="pg-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th className="droite">Op.</th>
                      <th className="droite">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.compte + c.nom}>
                        <td>
                          <div className="pg-client">
                            <div className="nom">{c.nom}</div>
                            <div className="cpt">{c.compte}</div>
                          </div>
                        </td>
                        <td className="droite num">{c.nbOperations}</td>
                        <td className="droite num">
                          <strong>{fcfa(c.volume)}</strong>
                          <div className="pg-note">
                            {c.verse > 0 && <span className="pg-pos">+{fcfa(c.verse)}</span>}
                            {c.verse > 0 && c.retire > 0 && ' · '}
                            {c.retire > 0 && <span className="pg-neg">−{fcfa(c.retire)}</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Motifs de retrait */}
            <section className="pg-carte">
              <div className="pg-carte-tete">
                <h2>Motifs de retrait</h2>
                <span>Répartition des montants retirés</span>
              </div>
              <div className="pg-carte-corps">
                {motifsRetrait.length === 0 ? (
                  <p className="pg-note">Aucun retrait sur la période.</p>
                ) : (
                  <div className="pg-barres">
                    {motifsRetrait.map((m) => (
                      <div key={m.motif} className="pg-barre">
                        <div className="lig">
                          <span title={m.motif}>
                            {m.motif} <span className="pg-note">({m.nb})</span>
                          </span>
                          <span>{fcfa(m.montant)}</span>
                        </div>
                        <div className="piste">
                          <div className="remplissage" style={{ width: `${(m.montant / (motifsRetrait[0]?.montant || 1)) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </>
      )}

      <p className="pg-note">
        Source : historique CBS des opérations enregistrées par votre code caissier. Le rapport imprimé comporte les cases de signature du caissier
        et du chef d&apos;agence.
      </p>
    </div>
  );
}

function Kpi({
  ton,
  icone,
  titre,
  valeur,
  sous,
  classeValeur,
  chargement,
}: {
  ton: 'vert' | 'rose' | 'bleu' | 'gris';
  icone: React.ReactNode;
  titre: string;
  valeur: string;
  sous: string;
  classeValeur?: string;
  chargement?: boolean;
}) {
  return (
    <div className={`pg-kpi ${ton}`}>
      <div className="haut">
        <span>{titre}</span>
        <span className="pastille">{icone}</span>
      </div>
      {chargement ? <div className="pg-squelette" style={{ height: 24, width: '70%' }} /> : <div className={`val ${classeValeur ?? ''}`}>{valeur}</div>}
      <div className="sous">{sous}</div>
    </div>
  );
}

/** Rapport A4 imprimable (arrêté d'activité de caisse) */
function htmlRapport({
  periode,
  caissier,
  code,
  k,
  jours,
  clients,
  motifsRetrait,
}: {
  periode: string;
  caissier: string;
  code: string;
  k: ReturnType<typeof totaux>;
  jours: LigneJour[];
  clients: ReturnType<typeof principauxClients>;
  motifsRetrait: ReturnType<typeof parMotif>;
}): string {
  const logo = new URL(logoUnacoopec, window.location.href).href;
  const e = echapperHtml;
  const lignesJours = [...jours]
    .reverse()
    .map(
      (j) => `<tr><td>${e(libelleJour(j.jour))}</td><td class="d">${j.nbVersements}</td><td class="d pos">${fcfa(j.montantVersements)}</td>
      <td class="d">${j.nbRetraits}</td><td class="d neg">${fcfa(j.montantRetraits)}</td><td class="d ${j.net >= 0 ? 'pos' : 'neg'}">${fcfa(j.net)}</td></tr>`,
    )
    .join('');
  const lignesClients = clients
    .map((c) => `<tr><td>${e(c.nom)}<br><small>${e(c.compte)}</small></td><td class="d">${c.nbOperations}</td><td class="d">${fcfa(c.volume)}</td></tr>`)
    .join('');
  const lignesMotifs = motifsRetrait
    .map((m) => `<tr><td>${e(m.motif)}</td><td class="d">${m.nb}</td><td class="d">${fcfa(m.montant)}</td></tr>`)
    .join('') || '<tr><td colspan="3">Aucun retrait</td></tr>';
  const edite = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(new Date());
  const premiereDate = jours.length ? dateCourte(depuisIso(jours[jours.length - 1].jour)) : '';
  const derniereDate = jours.length ? dateCourte(depuisIso(jours[0].jour)) : '';

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Rapport d'activité de caisse</title>
<style>
  @page { size: A4 portrait; margin: 14mm 14mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; color: #111827; font-size: 9.5pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  header { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0B677E; padding-bottom: 8px; }
  header img { height: 40px; } header .t { flex: 1; } header .org { font-size: 15pt; font-weight: 700; color: #0B677E; }
  header .sous { color: #6b7280; } .meta { text-align: right; font-size: 8.5pt; color: #4b5563; line-height: 1.5; }
  h1 { margin: 14px 0 2px; font-size: 14pt; text-transform: uppercase; letter-spacing: .5px; }
  .periode { color: #4b5563; margin-bottom: 12px; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; }
  .kpi { border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; }
  .kpi span { display: block; font-size: 7.5pt; text-transform: uppercase; color: #6b7280; letter-spacing: .4px; }
  .kpi b { display: block; font-size: 12pt; margin: 2px 0; } .kpi small { color: #6b7280; }
  h2 { font-size: 10.5pt; margin: 14px 0 6px; color: #0B677E; }
  table { width: 100%; border-collapse: collapse; } th, td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 7.5pt; text-transform: uppercase; color: #4b5563; }
  tfoot td { font-weight: 700; background: #f9fafb; border-top: 2px solid #d1d5db; }
  .d { text-align: right; white-space: nowrap; } .pos { color: #016951; } .neg { color: #e11d48; } small { color: #6b7280; }
  .deux { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 22px; page-break-inside: avoid; }
  .signatures div { border: 1px dashed #9ca3af; border-radius: 6px; height: 26mm; padding: 6px 8px; font-size: 8.5pt; color: #4b5563; }
  footer { margin-top: 12px; font-size: 7.5pt; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 6px; }
  tr { page-break-inside: avoid; }
</style></head><body>
<header>
  <img src="${e(logo)}" alt="">
  <div class="t"><div class="org">UNACOOPEC</div><div class="sous">BIOTRANSAC — Gestion de trésorerie</div></div>
  <div class="meta">Caissier : <b>${e(caissier || code)}</b>${code && caissier ? ` (${e(code)})` : ''}<br>Édité le ${e(edite)}</div>
</header>
<h1>Rapport d'activité de caisse</h1>
<div class="periode">${e(periode)}${premiereDate ? ` · activité du ${premiereDate} au ${derniereDate}` : ''}</div>
<div class="kpis">
  <div class="kpi"><span>Total versé</span><b class="pos">${fcfa(k.montantVersements)}</b><small>${k.nbVersements} versement(s)</small></div>
  <div class="kpi"><span>Total retiré</span><b class="neg">${fcfa(k.montantRetraits)}</b><small>${k.nbRetraits} retrait(s)</small></div>
  <div class="kpi"><span>Flux net</span><b class="${k.net >= 0 ? 'pos' : 'neg'}">${fcfa(k.net)}</b><small>Versements − retraits</small></div>
  <div class="kpi"><span>Opérations</span><b>${k.nbOperations}</b><small>${k.plusGrosse ? `max. ${fcfa(k.plusGrosse.amount)}` : ''}</small></div>
</div>
<h2>Récapitulatif par jour</h2>
<table><thead><tr><th>Date</th><th class="d">Vers.</th><th class="d">Montant versé</th><th class="d">Retr.</th><th class="d">Montant retiré</th><th class="d">Flux net</th></tr></thead>
<tbody>${lignesJours}</tbody>
<tfoot><tr><td>Total</td><td class="d">${k.nbVersements}</td><td class="d pos">${fcfa(k.montantVersements)}</td><td class="d">${k.nbRetraits}</td><td class="d neg">${fcfa(k.montantRetraits)}</td><td class="d">${fcfa(k.net)}</td></tr></tfoot></table>
<div class="deux">
  <div><h2>Principaux clients</h2><table><thead><tr><th>Client</th><th class="d">Op.</th><th class="d">Volume</th></tr></thead><tbody>${lignesClients}</tbody></table></div>
  <div><h2>Motifs de retrait</h2><table><thead><tr><th>Motif</th><th class="d">Nb</th><th class="d">Montant</th></tr></thead><tbody>${lignesMotifs}</tbody></table></div>
</div>
<div class="signatures"><div>Le caissier<br><small>Nom, date et signature</small></div><div>Le chef d'agence<br><small>Visa et cachet</small></div></div>
<footer>Document généré par BIOTRANSAC à partir de l'historique CBS des opérations du caissier. Montants en francs CFA.</footer>
</body></html>`;
}
